import { useSelector } from 'react-redux';

import { isEqual } from 'lodash-es';

import * as AccountStore from '@asteria/datalayer/stores/accounts';
import * as AppStore from '@asteria/datalayer/stores/app';

/**
 * @typedef { import('./types').Props } Props
 * @typedef { import('./types').ChildrenProps } ChildrenProps
 */

/**
 * @param { import('@tanstack/react-query').UseQueryResult } query
 */
export function useLinkedValues(query) {
	const links = query?.data?.links ?? [];

	const invoiceLink = links.find(({ type }) => type === 'INVOICE_PAYMENT');
	const bankTransactionLink = links.find(
		({ type }) => type === 'BANK_TRANSACTION',
	);

	const client = useSelector((store) => {
		const id =
			links.find(({ type }) => type === 'CLIENT')?.id ??
			invoiceLink?.clientId ??
			invoiceLink?.invoiceDetails?.clientId ??
			invoiceLink?.invoiceDetails?.client?.id;

		return AppStore.selectors.client(store, id);
	}, isEqual);

	const bankAccount = useSelector(
		(store) =>
			AccountStore.selectors.bankAccount(
				store,
				bankTransactionLink?.accountDetails?.bankAccountId,
			),
		isEqual,
	);

	return {
		invoice: invoiceLink?.invoiceDetails ?? null,
		bankTransaction: bankTransactionLink?.accountDetails ?? null,
		bankAccount: bankAccount ?? null,
		client: client ?? null,
	};
}

/**
 * @param { import('@tanstack/react-query').UseQueryResult } query
 */
export function useSections(query) {
	const links = useLinkedValues(query);

	const object = query?.data;

	return [
		{
			type: 'overview',
			children: [
				{ key: 'transactionDate', value: object?.paymentDate },
				{
					key: 'date.booked',
					value: links.bankTransaction?.dates?.booked,
				},
				{ key: 'client', value: links.client },
				{ key: 'status', value: object?.status },
				{
					key: 'bank.reference',
					value: links.bankTransaction?.identifiers?.number,
				},
				{ key: 'reference', value: links.invoice?.meta?.reference },
				{ key: 'message', value: links.invoice?.meta?.message },
				{
					key: 'tags',
					value: (object?.meta?.tags ?? []).map(
						(object) => object?._id ?? object?.id,
					),
				},
			].filter(({ value }) =>
				Array.isArray(value) ? value?.length : value,
			),
		},
		{
			type: 'risk',
			children: [
				{
					key: 'currencyrisk.current',
					value:
						object?.oracle?.currency?.risk &&
						object?.sums?.display?.currency !==
							object?.sums?.original?.currency,
				},
				{
					key: 'currencyrisk.sent',
					value:
						object?.oracle?.currency?.risk &&
						object?.sums?.display?.currency !==
							object?.sums?.original?.currency &&
						links.invoice,
				},
			].filter(({ value }) =>
				Array.isArray(value) ? value?.length : value,
			),
		},
		{
			type: 'account',
			children: [
				{
					key: 'account',
					value: links?.bankAccount?.identifiers?.number,
				},
				{ key: 'account.name', value: links?.bankAccount?.name },
				{
					key: 'account.clearing',
					value: links?.bankAccount?.identifiers?.clearing,
				},
				{
					key: 'account.bic',
					value: links?.bankAccount?.identifiers?.bic,
				},
				{
					key: 'account.iban',
					value: links?.bankAccount?.identifiers?.iban,
				},
				{
					key: 'currency',
					value: links?.bankAccount?.sums?.original?.currency,
				},
			].filter(({ value }) =>
				Array.isArray(value) ? value?.length : value,
			),
		},
	].filter(({ children }) => children?.length);
}
